import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LocaleProvider } from "@/i18n";
import ErrorBoundary from "@/components/ErrorBoundary";
import { initGA4 } from "@/lib/analytics";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import EditorPage from "@/pages/EditorPage";
import CsvBatchPage from "@/pages/CsvBatchPage";
import AcroFormPage from "@/pages/AcroFormPage";
import AdminPage from "@/pages/AdminPage";
import SubscribePage from "@/pages/SubscribePage";
import SubscribeSuccessPage from "@/pages/SubscribeSuccessPage";
import SubscribeCancelPage from "@/pages/SubscribeCancelPage";
import { Toaster } from "@/components/ui/sonner";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-xs text-[#757575]">Caricamento…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Gate: users without access are pushed to /subscribe (super-admin bypasses).
  const isSubscribeRoute = loc.pathname.startsWith("/subscribe");
  const isAdminRoute = loc.pathname.startsWith("/admin");
  if (!user.has_access && !user.is_super_admin && !isSubscribeRoute && !isAdminRoute) {
    return <Navigate to="/subscribe" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.is_super_admin) return <Navigate to="/admin" replace />;
    if (!user.has_access) return <Navigate to="/subscribe" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

// Super-admins land on /admin after login (see PublicOnly). They can navigate
// to any editor mode via the tabs in AppShell. No forced-redirect gate on the
// home route — super admins are also allowed to work with the PDF batch tools.

function App() {
  useEffect(() => {
    initGA4();
  }, []);
  return (
    <div className="App">
      <ErrorBoundary>
        <LocaleProvider>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
                <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
                <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
                <Route path="/reset-password" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
                <Route path="/subscribe" element={<ProtectedRoute><SubscribePage /></ProtectedRoute>} />
                <Route path="/subscribe/success" element={<SubscribeSuccessPage />} />
                <Route path="/subscribe/cancel" element={<SubscribeCancelPage />} />
                <Route path="/" element={<ProtectedRoute><EditorPage /></ProtectedRoute>} />
                <Route path="/csv" element={<ProtectedRoute><CsvBatchPage /></ProtectedRoute>} />
                <Route path="/form" element={<ProtectedRoute><AcroFormPage /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </LocaleProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
